import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
    Menu, X, ChevronDown, ChevronRight, Search,
    Sparkles, Cloud, ShieldCheck, Code2, Layers,
    CheckSquare, Compass, Users, GraduationCap, Briefcase, LineChart, ArrowUpRight,
} from "lucide-react";
import {
    LOGO_URL, PRODUCTS,
    SOLUTIONS_TRAINING, SOLUTIONS_TALENT,
    LEARNING_TRACKS, TECH_PARTNERS,
} from "@/lib/data";
import CatalogueModal from "@/components/ui-shared/CatalogueModal";
import SearchModal from "@/components/ui-shared/SearchModal";

const trackIconMap = {
    sparkles: Sparkles,
    database: LineChart,
    cloud: Cloud,
    shield: ShieldCheck,
    code: Code2,
    layers: Layers,
    "check-circle": CheckSquare,
    compass: Compass,
    users: Users,
};

// Positions the dropdown panel below the nav, centered on viewport, clamped to screen edges
const DropdownPanel = ({ children, maxWidth = 960, onMouseEnter, onMouseLeave }) => {
    const ref = useRef(null);

    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        const vw = window.innerWidth;
        const w = Math.min(vw * 0.92, maxWidth);
        const padding = 16;
        let left = (vw - w) / 2;
        left = Math.max(padding, Math.min(left, vw - w - padding));
        el.style.width = `${w}px`;
        el.style.left = `${left}px`;
    }, [maxWidth]);

    return (
        <div
            ref={ref}
            style={{ position: "fixed", top: "88px" }}
            className="pt-3 z-50 animate-fade-up"
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            {children}
        </div>
    );
};

// helper: mega link with title + description
const MegaLink = ({ to, title, description, icon: Icon }) => (
    <Link
        to={to}
        data-testid={`mega-link-${title.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}
        className="group flex items-start gap-3 rounded-xl p-3 hover:bg-white/[0.04] transition-colors"
    >
        {Icon && (
            <div className="mt-0.5 shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-[#A100FF]/30 to-[#7B00CC]/10 border border-[#C266FF]/25 flex items-center justify-center">
                <Icon className="w-4 h-4 text-[#E9CCFF]" />
            </div>
        )}
        <div className="min-w-0">
            <div className="font-display text-white text-sm font-semibold tracking-tight group-hover:text-[#E9CCFF] transition-colors">
                {title}
            </div>
            {description && (
                <div className="text-[12.5px] text-[#B0A3C4] mt-0.5 truncate">{description}</div>
            )}
        </div>
    </Link>
);

const Featured = ({ title, desc, cta, to, external = false }) => (
    <div className="rounded-xl p-5 bg-gradient-to-br from-[#A100FF]/15 to-[#7B00CC]/[0.05] border border-[#C266FF]/25 relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-[#A100FF]/30 blur-3xl" />
        <div className="relative">
            <div className="overline mb-3"><span className="dot" /> Featured</div>
            <div className="font-display text-white text-lg font-semibold tracking-tight">{title}</div>
            <p className="text-[#B0A3C4] text-[13px] leading-relaxed mt-1">{desc}</p>
            <Link to={to} className="inline-flex items-center gap-2 mt-4 text-[#E9CCFF] hover:text-white font-display font-medium text-sm">
                {cta} <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
        </div>
    </div>
);

const Header = ({ onOpenCatalogue }) => {
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [mobileGroup, setMobileGroup] = useState(null);
    const [activeMenu, setActiveMenu] = useState(null);
    const location = useLocation();
    const closeTimerRef = useRef(null);

    const openMenu = (key) => {
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
        setActiveMenu(key);
    };

    const scheduleCloseMenu = () => {
        if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
        closeTimerRef.current = setTimeout(() => {
            setActiveMenu(null);
            closeTimerRef.current = null;
        }, 200);
    };

    useEffect(() => () => {
        if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    }, []);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setMobileOpen(false);
        setActiveMenu(null);
        setMobileGroup(null);
    }, [location.pathname, location.hash]);

    const productIcons = [Sparkles, GraduationCap, Cloud, CheckSquare, Code2];

    return (
        <>
            <header
                data-testid="site-header"
                className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b ${
                    scrolled
                        ? "bg-[#07020F]/95 backdrop-blur-xl border-white/[0.08] shadow-[0_10px_40px_-15px_rgba(0,0,0,0.6)]"
                        : "bg-[#07020F] border-transparent"
                }`}
            >
                <div className="sxe-container flex items-center justify-between h-[88px]">
                    {/* Logo (larger) */}
                    <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
                        <img
                            src={LOGO_URL}
                            alt="SpringXE"
                            className="h-14 md:h-16 w-auto object-contain transition-transform group-hover:scale-[1.03]"
                        />
                    </Link>

                    {/* Desktop nav */}
                    <nav
                        className="hidden lg:flex items-center gap-1"
                        onMouseLeave={scheduleCloseMenu}
                    >
                        {/* Products */}
                        <div className="static" onMouseEnter={() => openMenu("products")}>
                            <button
                                data-testid="nav-btn-products"
                                className={`flex items-center gap-1 px-4 py-2 font-display text-[15px] font-medium rounded-full transition-colors ${activeMenu === "products" ? "text-white bg-white/[0.06]" : "text-[#D8CDE8] hover:text-white"}`}
                            >
                                Products
                                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${activeMenu === "products" ? "rotate-180" : ""}`} />
                            </button>
                            {activeMenu === "products" && (
                                <DropdownPanel maxWidth={960} onMouseEnter={() => openMenu("products")} onMouseLeave={scheduleCloseMenu}>
                                    <div className="rounded-2xl p-6 border border-white/[0.08] bg-[#0d0621]/95 backdrop-blur-xl shadow-2xl grid grid-cols-3 gap-6">
                                        <div className="col-span-2">
                                            <div className="overline mb-3"><span className="dot" /> Platform</div>
                                            <div className="grid grid-cols-1 gap-1">
                                                {PRODUCTS.map((p, i) => {
                                                    const Icon = productIcons[i] || Sparkles;
                                                    return (
                                                        <MegaLink
                                                            key={p.slug}
                                                            to={`/products/${p.slug}`}
                                                            title={p.title}
                                                            description={p.tagline}
                                                            icon={Icon}
                                                        />
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        <Featured
                                            title="Enterprise LxP"
                                            desc="Xelerate skill velocity across your entire workforce with an AI-native LxP."
                                            cta="Explore Platform"
                                            to="/products/enterprise-lxp"
                                        />
                                    </div>
                                </DropdownPanel>
                            )}
                        </div>

                        {/* Solutions */}
                        <div className="static" onMouseEnter={() => openMenu("solutions")}>
                            <button
                                data-testid="nav-btn-solutions"
                                className={`flex items-center gap-1 px-4 py-2 font-display text-[15px] font-medium rounded-full transition-colors ${activeMenu === "solutions" ? "text-white bg-white/[0.06]" : "text-[#D8CDE8] hover:text-white"}`}
                            >
                                Solutions
                                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${activeMenu === "solutions" ? "rotate-180" : ""}`} />
                            </button>
                            {activeMenu === "solutions" && (
                                <DropdownPanel maxWidth={1000} onMouseEnter={() => openMenu("solutions")} onMouseLeave={scheduleCloseMenu}>
                                    <div className="rounded-2xl p-6 border border-white/[0.08] bg-[#0d0621]/95 backdrop-blur-xl shadow-2xl grid grid-cols-3 gap-6">
                                        <div>
                                            <div className="overline mb-3"><span className="dot" /> Training</div>
                                            <div className="flex flex-col gap-1">
                                                {SOLUTIONS_TRAINING.map((s) => (
                                                    <MegaLink key={s.slug} to={`/solutions/${s.slug}`} title={s.title} icon={GraduationCap} />
                                                ))}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="overline mb-3"><span className="dot" /> Talent</div>
                                            <div className="flex flex-col gap-1">
                                                {SOLUTIONS_TALENT.map((s) => (
                                                    <MegaLink key={s.slug} to={`/solutions/${s.slug}`} title={s.title} icon={Briefcase} />
                                                ))}
                                            </div>
                                        </div>
                                        <Featured
                                            title="Hire–Train–Deploy"
                                            desc="Custom niche talent pipelines — sourced, trained, and deployed on your stack with SLA-backed outcomes."
                                            cta="Talk to Our Experts"
                                            to="/solutions/hire-train-deploy"
                                        />
                                    </div>
                                </DropdownPanel>
                            )}
                        </div>

                        {/* Partners */}
                        <div className="relative" onMouseEnter={() => openMenu("partners")}>                            <button
                                data-testid="nav-btn-partners"
                                className={`flex items-center gap-1 px-4 py-2 font-display text-[15px] font-medium rounded-full transition-colors ${activeMenu === "partners" ? "text-white bg-white/[0.06]" : "text-[#D8CDE8] hover:text-white"}`}
                            >
                                Partner Offerings
                                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${activeMenu === "partners" ? "rotate-180" : ""}`} />
                            </button>
                            {activeMenu === "partners" && (
                                <DropdownPanel maxWidth={780} onMouseEnter={() => openMenu("partners")} onMouseLeave={scheduleCloseMenu}>
                                    <div className="rounded-2xl p-6 border border-white/[0.08] bg-[#0d0621]/95 backdrop-blur-xl shadow-2xl grid grid-cols-[1.4fr_1fr] gap-6">
                                        <div>
                                            <div className="overline mb-3"><span className="dot" /> 28+ Global Partners</div>
                                            <div className="grid grid-cols-2 gap-x-4 gap-y-1 max-h-[280px] overflow-hidden">
                                                {TECH_PARTNERS.slice(0, 20).map((p) => (
                                                    <Link
                                                        key={p.name}
                                                        to={p.slug ? `/partners/${p.slug}` : "/partners"}
                                                        className="text-[13px] text-[#B0A3C4] hover:text-white py-1 truncate"
                                                    >
                                                        · {p.name}
                                                    </Link>
                                                ))}
                                            </div>
                                            <Link to="/partners" className="inline-flex items-center gap-2 mt-4 text-[#E9CCFF] hover:text-white font-display font-medium text-sm" data-testid="mega-partners-explore">
                                                Explore All Partners <ArrowUpRight className="w-3.5 h-3.5" />
                                            </Link>
                                        </div>
                                        <Featured
                                            title="Certified Ecosystem"
                                            desc="SpringXE certifications aligned with AWS, Microsoft, Google Cloud, SAP, Oracle, IBM & more."
                                            cta="View Partners"
                                            to="/partners"
                                        />
                                    </div>
                                </DropdownPanel>
                            )}
                        </div>

                        {/* Learning Tracks — highlighted CTA button */}
                        <NavLink
                            to="/learning-tracks"
                            data-testid="nav-btn-learning-tracks"
                            className={({ isActive }) =>
                                `ml-2 inline-flex items-center gap-1.5 px-5 py-2.5 font-display text-[14px] font-semibold rounded-full transition-all duration-300 shadow-[0_10px_24px_-10px_rgba(161,0,255,0.6)] ${
                                    isActive
                                        ? "bg-white text-[#7B00CC]"
                                        : "bg-gradient-to-r from-[#A100FF] to-[#7B00CC] text-white hover:shadow-[0_14px_30px_-10px_rgba(161,0,255,0.85)] hover:brightness-110"
                                }`
                            }
                        >
                            Learning Tracks
                        </NavLink>

                        {/* Search */}
                        <button
                            data-testid="nav-btn-search"
                            aria-label="Search"
                            onClick={() => setSearchOpen(true)}
                            className="ml-1 inline-flex items-center justify-center w-10 h-10 rounded-full text-[#D8CDE8] hover:text-white hover:bg-white/[0.06] transition-colors"
                        >
                            <Search className="w-[18px] h-[18px]" />
                        </button>
                    </nav>

                    {/* Mobile: search + menu buttons */}
                    <div className="lg:hidden flex items-center gap-2">
                        <button
                            data-testid="mobile-search-toggle"
                            aria-label="Search"
                            className="inline-flex items-center justify-center w-11 h-11 rounded-full border border-white/10 text-white"
                            onClick={() => setSearchOpen(true)}
                        >
                            <Search className="w-5 h-5" />
                        </button>
                        <button
                            data-testid="mobile-menu-toggle"
                            className="inline-flex items-center justify-center w-11 h-11 rounded-full border border-white/10 text-white"
                            onClick={() => setMobileOpen((v) => !v)}
                            aria-label="Toggle menu"
                        >
                            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                        </button>
                    </div>
                </div>

                {/* Mobile drawer */}
                {mobileOpen && (
                    <div className="lg:hidden bg-[#07020F]/98 backdrop-blur-xl border-t border-white/[0.08] animate-fade-up">
                        <div className="sxe-container py-6 flex flex-col gap-1 max-h-[80vh] overflow-y-auto">
                            <NavLink to="/" end data-testid="mobile-nav-home" className={({ isActive }) => `px-4 py-3 rounded-xl font-display text-base ${isActive ? "bg-white/[0.05] text-white" : "text-[#D8CDE8] hover:text-white hover:bg-white/[0.03]"}`}>Home</NavLink>

                            {/* Mobile groups */}
                            {[
                                { key: "products", label: "Products", items: PRODUCTS.map(p => ({ to: `/products/${p.slug}`, label: p.title })), overview: { to: "/products", label: "All Products" } },
                                { key: "solutions", label: "Solutions", items: [...SOLUTIONS_TRAINING, ...SOLUTIONS_TALENT].map(s => ({ to: `/solutions/${s.slug}`, label: s.title })), overview: { to: "/solutions", label: "All Solutions" } },
                            ].map((g) => (
                                <div key={g.key}>
                                    <button
                                        onClick={() => setMobileGroup(mobileGroup === g.key ? null : g.key)}
                                        className="w-full flex items-center justify-between px-4 py-3 rounded-xl font-display text-base text-[#D8CDE8] hover:text-white hover:bg-white/[0.03]"
                                    >
                                        {g.label}
                                        <ChevronDown className={`w-4 h-4 transition-transform ${mobileGroup === g.key ? "rotate-180" : ""}`} />
                                    </button>
                                    {mobileGroup === g.key && (
                                        <div className="ml-4 border-l border-white/[0.08] pl-4 py-1 flex flex-col">
                                            <Link to={g.overview.to} className="py-2 text-[#E9CCFF] font-medium">{g.overview.label} →</Link>
                                            {g.items.map((it) => (
                                                <Link key={it.to} to={it.to} className="py-2 text-[13.5px] text-[#B0A3C4] hover:text-white">
                                                    {it.label}
                                                </Link>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}

                            <NavLink to="/learning-tracks" data-testid="mobile-nav-learning-tracks" className={({ isActive }) => `px-4 py-3 rounded-xl font-display text-base ${isActive ? "bg-white/[0.05] text-white" : "text-[#D8CDE8] hover:text-white hover:bg-white/[0.03]"}`}>Learning Tracks</NavLink>
                            <NavLink to="/partners" data-testid="mobile-nav-partners" className={({ isActive }) => `px-4 py-3 rounded-xl font-display text-base ${isActive ? "bg-white/[0.05] text-white" : "text-[#D8CDE8] hover:text-white hover:bg-white/[0.03]"}`}>Partner Offerings</NavLink>
                        </div>
                    </div>
                )}
            </header>
            <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
        </>
    );
};

export default Header;
